import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Table } from "reactstrap";
import { CART_COLUMNS, cartCellLabel } from "./columns";
import { useTable } from "react-table";
import { connect } from "react-redux";
import { getCartTotalPointsOrDollars, getRedemptionValue, isForeignCurrency } from "@/shared/helpers";
import TemplateButton from "@/shared/components/TemplateButton";
import { useTranslation } from "react-i18next";
import { getMerchant } from "@/services/program/getMerchant";
import { onClickRemoveItem } from "@/services/cart/removeItem";

import { gcmCartBusiness } from "@/containers/Participant/GcmShared/helpers";
const API_STORAGE_URL = `${process.env.REACT_APP_API_STORAGE_URL}`;

const CartPage = ({ cart, program, pointBalance, currency }) => {
  const { t } = useTranslation();
  const isLoading = false;
  const [cartIsEmpty, setCartIsEmpty] = useState(true);
  const [cartObject, setCartObject] = useState({
    items: [],
  });
  const [tableData, setTableData] = useState([]);
  const [totalPoints, setTotalPoints] = useState(0)

  useEffect(() => {
    if (!cart?.items?.length) {
      setTableData([]);
      setTotalPoints(0)
      return;
    }

    (async () => {
      const results = await Promise.all(
        cart.items.map(async (item, itemIndex) => {
          const merchant = await getMerchant(program.organization_id, program.id, item.merchant_id);
let redemptionValue = getRedemptionValue(item, merchant.pivot);
          let giftCode = `$${parseFloat(item.sku_value).toFixed(2)} ${t('gift_code')} = ${Math.floor(redemptionValue * program.factor_valuation)}`
          if( isForeignCurrency(currency) )  {
            // redemptionValue = parseFloat(redemptionValue * currency.conversion_rate).toFixed(2)
            redemptionValue = parseFloat(redemptionValue).toFixed(2)
            giftCode = `${currency.symbol}${parseFloat(item.sku_value).toFixed(2)} ${t('gift_code')} = ${parseFloat(1/currency.conversion_rate * redemptionValue * program.factor_valuation).toFixed(0)}`
          }
          return {
            redemptionValue: isForeignCurrency(currency) ? (1/currency.conversion_rate * redemptionValue) : redemptionValue,
            rowData: {
              logo: (() => {
                if (String(item.merchant_id).startsWith('gcm-')) return gcmCartBusiness(item, merchant);
                return `${API_STORAGE_URL}/${item.merchant_icon}`;
              })(),
              // Gift Card Market requires its product name format
              name: String(item.merchant_id).startsWith('gcm-')
                ? t('gcm_local_gift_card', { name: item.merchant_name })
                : item.merchant_name,
              giftCode: giftCode,
              quantity: item.qty,
              price: parseFloat((isForeignCurrency(currency) ? (1/currency.conversion_rate * redemptionValue) : redemptionValue) * program.factor_valuation).toFixed(0),
              total: parseFloat((isForeignCurrency(currency) ? (1/currency.conversion_rate * redemptionValue) : redemptionValue) * program.factor_valuation).toFixed(0) * item.qty,
              _cartIndex: itemIndex,
            }
          };
        })
      );

      const redemptionValues = results.map(r => r.redemptionValue);
      const tableData = results.map(r => r.rowData);

      setTableData(tableData);
      const total_points = getCartTotalPointsOrDollars(cart?.items, program.factor_valuation, redemptionValues);
      setTotalPoints(Math.ceil(total_points))
    })();
  }, [cart, program, currency, t]);

  useEffect(() => {
    if (cart) {
      prepareCart(cart);
    }
  }, [cart]);

  const prepareCart = (cartdata) => {
    if (cartdata?.items && cartdata.items.length > 0) {
      setCartIsEmpty(false);
    } else {
      setCartIsEmpty(true);
    }
    setCartObject(cartdata ?? { items: [] });
  };

  const columns = React.useMemo(
    () => [
      ...CART_COLUMNS,
      {
        Header: "",
        id: "remove",
        Cell: ({ row }) => (
          <button
            type="button"
            className="rc-panel__remove"
            onClick={() => onClickRemoveItem(row.original._cartIndex)}
          >
            {t("remove")}
          </button>
        ),
      },
    ],
    [t]
  );
  const { getTableProps, headerGroups, rows, prepareRow } = useTable({
    columns,
    data: tableData,
  });
  const navigate = useNavigate();
  const hasItems = cartObject?.items?.length > 0;
  const columnCount = headerGroups[0]?.headers?.length ?? columns.length;
  const balanceAfterPurchase = Math.ceil((pointBalance?.pointBalance ?? 0) - totalPoints);

  return (
    <section className="rc-panel">
      {cartIsEmpty ? (
        <div className="rc-panel__empty">{t("cart_empty")}</div>
      ) : (
      <div className="rc-panel__table-wrap">
        <Table responsive size="md" className="rc-panel__table table--stack-mobile" {...getTableProps()}>
          <thead>
            {headerGroups.map((headerGroup) => {
              const { key, ...headerGroupProps } = headerGroup.getHeaderGroupProps();

              return (
                <tr key={key} {...headerGroupProps}>
                  {headerGroup.headers.map((column) => {
                    const { key: headerKey, ...headerProps } = column.getHeaderProps();

                    return (
                      <th key={headerKey} {...headerProps}>
                        {t(column.Header)}
                      </th>
                    );
                  })}
                </tr>
              );
            })}
          </thead>
          <tbody>
            {rows.map((row) => {
              prepareRow(row);
              const { key, ...rowProps } = row.getRowProps();

              return (
                <tr key={key} {...rowProps}>
                  {row.cells.map((cell) => {
                    const { key: cellKey, ...cellProps } = cell.getCellProps();

                    return (
                      <td
                        key={cellKey}
                        className={`cart_item${cell.column.id === "logo" ? " cart_item--logo" : ""}`}
                        {...cellProps}
                        data-label={cartCellLabel(cell.column, t)}
                      >
                        {cell.render("Cell")}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>
      )}

      {hasItems && (
        <div className="rc-panel__footer">
          <div className="rc-panel__summary">
            <div className="rc-panel__summary-item">
              <span className="rc-panel__summary-label">{t("total")}</span>
              <strong className="rc-panel__summary-value">
                {totalPoints.toLocaleString()} {t("points")}
              </strong>
            </div>
            <div className="rc-panel__summary-item">
              <span className="rc-panel__summary-label">{t("balance_after_purchase")}</span>
              <strong className="rc-panel__summary-value">
                {balanceAfterPurchase.toLocaleString()} {t("points")}
              </strong>
            </div>
          </div>

          <div className="rc-panel__actions">
            <TemplateButton
              type="submit"
              disabled={isLoading}
              onClick={() => {
                navigate("/participant/checkout");
              }}
              text={t("checkout")}
            />
          </div>
        </div>
      )}
    </section>
  );
};

export default connect((state) => ({
  cart: state.cart,
  program: state.program,
  pointBalance: state.pointBalance,
  currency: state.currency
}))(CartPage);
